/**
 * Flash utility for shadow-DOM-aware element attention animation.
 *
 * Usage: inject flash.css into the element's adoptedStyleSheets, then in
 * connectedCallback bind: `(e) => handleFlashEvent(this, 'my-tag-name', e)`
 * and listen for 'as-flash-element' on window.
 *
 * If the target element has an as-accordion ancestor, that accordion is flashed
 * instead (and opened if closed). Falls back to flashing the element itself.
 */

function openParentAccordions(el: Element): HTMLElement | null {
  let current: Element | null = el.parentElement;
  let root: Node = el.getRootNode();
  let firstAccordion: HTMLElement | null = null;

  while (current) {
    if (current.tagName === 'AS-ACCORDION') {
      if (!firstAccordion) firstAccordion = current as HTMLElement;
      current.setAttribute('open', '');
    }
    if (current.parentElement) {
      current = current.parentElement;
    } else if (root instanceof ShadowRoot) {
      current = root.host;
      root = current.getRootNode();
    } else {
      break;
    }
  }
  return firstAccordion;
}

export function handleFlashEvent(element: HTMLElement, tagName: string, event: Event): void {
  const { target } = (event as CustomEvent).detail || {};
  if (target !== tagName) return;
  const firstAccordion = openParentAccordions(element);
  const flashEl = firstAccordion ?? element;
  flashEl.classList.add('flashing');
  flashEl.addEventListener('animationend', () => flashEl.classList.remove('flashing'), { once: true });
}
